import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { Container, Paper, Typography, Box, Card, CardContent, CardActions, Button, IconButton, createTheme, ThemeProvider } from '@mui/material';
import {
    PictureAsPdf,
    Image,
    MergeType,
    CallSplit,
    Compress,
    Description,
    TableChart,
    ArrowBack,
    Code,
    DataObject,
    VideoFile,
    AddPhotoAlternate
} from '@mui/icons-material';
import Login from './components/Login';
import CssBaseline from '@mui/material/CssBaseline';
import FileConverter from './components/FileConverter';
import { ConversionType, ConversionOption } from './types';
import PasswordBox from './components/Password';
import SignatureBox from './components/signature';
import GenerateImage from './components/generateImage';
import SobreporFotoBox from './components/sobreporFoto';

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#2563eb',
            light: '#3b82f6',
            dark: '#1d4ed8',
        },
        secondary: {
            main: '#dc2626',
            light: '#ef4444',
            dark: '#b91c1c',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h3: {
            fontWeight: 700,
            color: '#ffffff',
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
        },
        h4: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 500,
        },
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    textTransform: 'none',
                    fontWeight: 600,
                    padding: '12px 24px',
                },
            },
        },
    },
});

const SOBREPOR_FOTO_PATH = '/6-anos-leve';

const conversionOptions: ConversionOption[] = [
    // {
    //     id: 'sobrepor-foto',
    //     label: '6 Anos da Leve Saúde',
    //     description: 'Coloque sua foto na arte comemorativa de 6 anos e baixe a arte em PNG',
    //     acceptedTypes: [],
    //     multiple: false,
    //     icon: Image,
    //     color: '#fb4c00',
    //     button: 'Gerar Imagem',
    //     path: SOBREPOR_FOTO_PATH,
    // },
    {
        id: 'password-generator',
        label: 'Gerador de Senhas',
        description: 'Gere senhas fortes e seguras para suas contas',
        acceptedTypes: [],
        multiple: false,
        icon: Description,
        color: '#360d01',
        button: 'Gerar Senha',
    },
    {
        id: 'docx-to-pdf',
        label: 'DOC para PDF',
        description: 'Converte documentos Word para PDF',
        acceptedTypes: ['.doc', '.docx'],
        multiple: false,
        icon: Description,
        color: '#3b82f6',
        button: 'Converter para PDF',
    },
    {
        id: 'pdf-to-docx',
        label: 'PDF para Word',
        description: 'Converte arquivos PDF para documentos Word',
        acceptedTypes: ['.pdf'],
        multiple: false,
        icon: Description,
        color: '#0f766e',
        button: 'Converter para Word',
    },
    {
        id: 'merge-pdfs',
        label: 'Unir PDFs',
        description: 'Une múltiplos PDFs em um único arquivo',
        acceptedTypes: ['.pdf'],
        multiple: true,
        icon: MergeType,
        color: '#ef4444',
        button: 'Unir PDFs',
    },
    {
        id: 'pdf-to-jpg',
        label: 'PDF para JPG',
        description: 'Converte páginas de PDF para imagens JPG',
        acceptedTypes: ['.pdf'],
        multiple: false,
        icon: Image,
        color: '#10b981',
        button: 'Converter para JPG',
    },
    {
        id: 'jpg-to-pdf',
        label: 'JPG para PDF',
        description: 'Converte imagens para PDF',
        acceptedTypes: ['.jpg', '.jpeg', '.png'],
        multiple: true,
        icon: PictureAsPdf,
        color: '#f59e0b',
        button: 'Converter para PDF',
    },
    {
        id: 'csv-to-pdf',
        label: 'CSV para PDF',
        description: 'Converte planilhas CSV para PDF',
        acceptedTypes: ['.csv'],
        multiple: false,
        icon: TableChart,
        color: '#8b5cf6',
        button: 'Converter para PDF',
    },
    {
        id: 'csv-to-xlsx',
        label: 'CSV para Excel',
        description: 'Converte planilhas CSV para Excel (XLSX)',
        acceptedTypes: ['.csv'],
        multiple: false,
        icon: TableChart,
        color: '#16a34a',
        button: 'Converter para Excel',
    },
    {
        id: 'excel-to-pdf',
        label: 'Excel para PDF',
        description: 'Converte planilhas Excel para PDF',
        acceptedTypes: ['.xlsx', '.xls'],
        multiple: false,
        icon: TableChart,
        color: '#06b6d4',
        button: 'Converter para PDF',
    },
    {
        id: 'pdf-to-excel',
        label: 'PDF para Excel',
        description: 'Extrai tabelas de PDF e converte para Excel',
        acceptedTypes: ['.pdf'],
        multiple: false,
        icon: TableChart,
        color: '#059669',
        button: 'Converter para Excel',
    },
    {
        id: 'pdf-to-base64',
        label: 'PDF para Base64',
        description: 'Converte PDF para formato Base64 (JSON)',
        acceptedTypes: ['.pdf'],
        multiple: false,
        icon: Code,
        color: '#7c3aed',
        button: 'Converter para Base64',
    },
    {
        id: 'base64-to-pdf',
        label: 'Base64 para PDF',
        description: 'Converte Base64 de volta para PDF',
        acceptedTypes: ['.json', '.txt'],
        multiple: false,
        icon: DataObject,
        color: '#dc2626',
        button: 'Converter para PDF',
    },
    {
        id: 'split-pdf',
        label: 'Dividir PDF',
        description: 'Divide um PDF em páginas individuais',
        acceptedTypes: ['.pdf'],
        multiple: false,
        icon: CallSplit,
        color: '#ec4899',
        button: 'Dividir PDF',
    },
    {
        id: 'compress-pdf',
        label: 'Comprimir PDF',
        description: 'Reduz o tamanho do arquivo PDF',
        acceptedTypes: ['.pdf'],
        multiple: false,
        icon: Compress,
        color: '#84cc16',
        button: 'Comprimir PDF',
    },
    {
        id: 'signature-generator',
        label: 'Gerador de Assinaturas',
        description: 'Gere assinaturas para seus documentos',
        acceptedTypes: [],
        multiple: false,
        icon: Image,
        color: '#8a7065',
        button: 'Gerar Assinatura',
    },
    {
        id: 'mp4-to-gif',
        label: 'MP4 para GIF',
        description: 'Converte vídeos MP4 para GIF animado',
        acceptedTypes: ['.mp4', '.mov', '.avi', '.mkv'],
        multiple: false,
        icon: VideoFile,
        color: '#e91e63',
        button: 'Converter para GIF',
    }
];

const MainApp: React.FC = () => {
    const { accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedConversion, setSelectedConversion] = useState<ConversionType | null>(null);

    const activeAccount = accounts[0];
    const userName = activeAccount?.name || '';
    const userEmail = activeAccount?.username || '';

    useEffect(() => {
        if (location.pathname === SOBREPOR_FOTO_PATH) {
            navigate('/');
            setSelectedConversion(null);
        }
    }, [location.pathname, navigate]);

    const handleSelectConversion = (conversionType: ConversionType) => {
        const option = conversionOptions.find(o => o.id === conversionType);
        if (option?.path) {
            navigate(option.path);
            setSelectedConversion(conversionType);
        } else {
            if (location.pathname !== '/') navigate('/');
            setSelectedConversion(conversionType);
        }
    };

    const handleBackToMenu = () => {
        navigate('/');
        setSelectedConversion(null);
    };

    if (!isAuthenticated) {
        return <Login />;
    }

    if (selectedConversion) {
        const option = conversionOptions.find(opt => opt.id === selectedConversion);

        if (!option) {
            return (
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="h6">Opção de conversão não encontrada</Typography>
                    </Box>
                </ThemeProvider>
            );
        }

        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box
                    sx={{
                        minHeight: '100vh',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        py: 4,
                    }}
                >
                    <Container maxWidth={option.id === 'image-generator' ? 'xl' : 'md'}>
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)',
                            }}
                        >
                            <Box sx={{ p: 3 }}>
                                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <IconButton
                                        onClick={handleBackToMenu}
                                        sx={{
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            '&:hover': { bgcolor: 'primary.dark' }
                                        }}
                                    >
                                        <ArrowBack />
                                    </IconButton>
                                    <Box>
                                        <Typography variant="h4" component="h1" gutterBottom>
                                            {option.label}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                            {option.description}
                                        </Typography>
                                    </Box>
                                </Box>
                                {option.id === 'password-generator' ? (
                                    <PasswordBox />
                                ) : option.id === 'signature-generator' ? (
                                    <SignatureBox />
                                ) : option.id === 'sobrepor-foto' ? (
                                    <SobreporFotoBox />
                                ) : option.id === 'image-generator' ? (
                                    <GenerateImage />
                                ) : (
                                    <FileConverter
                                        conversionType={selectedConversion}
                                        option={option}
                                    />
                                )}
                            </Box>
                        </Paper>
                    </Container>
                </Box>
            </ThemeProvider>
        );
    }


    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box
                sx={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    py: 6,
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant="h3"
                            component="h1"
                            gutterBottom
                            sx={{
                                mb: 2,
                                color: '#ffffff',
                                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                                fontWeight: 700,
                            }}
                        >
                            ILevePDF
                        </Typography>
                        <Typography
                            variant="h6"
                            component="h2"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontWeight: 400,
                                mb: 4,
                                textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                            }}
                        >
                            Converta e manipule seus documentos facilmente
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                                lg: 'repeat(4, 1fr)',
                            },
                            gap: 3,
                        }}
                    >
                        {conversionOptions.map((option) => {
                            const IconComponent = option.icon;
                            return (
                                <Card
                                    key={option.id}
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        cursor: 'pointer',
                                        background: option.id === 'image-generator' ? '#451e44' : 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                    }}
                                    onClick={() => handleSelectConversion(option.id)}
                                >
                                    <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: '50%',
                                                bgcolor: option.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                mb: 2,
                                                boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.15)',
                                            }}
                                        >
                                            <IconComponent sx={{ fontSize: 28, color: 'white' }} />
                                        </Box>
                                        <Typography variant="h6" component="h2" color={option.id === 'image-generator' ? 'white' : 'text.primary'} gutterBottom sx={{ fontWeight: 600 }}>
                                            {option.label}
                                        </Typography>
                                        <Typography variant="body2" color={option.id === 'image-generator' ? 'white' : 'text.secondary'} sx={{ mb: 2 }}>
                                            {option.description}
                                        </Typography>
                                        {option.acceptedTypes.length > 0 && (
                                            <Typography variant="caption" sx={{
                                                bgcolor: 'rgba(0, 0, 0, 0.05)',
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: 2,
                                                display: 'inline-block'
                                            }}>
                                                {option.acceptedTypes.join(', ')}
                                            </Typography>
                                        )}
                                    </CardContent>
                                    <CardActions sx={{ p: 2 }}>
                                        <Button
                                            size="large"
                                            variant="contained"
                                            fullWidth
                                            sx={{
                                                bgcolor: option.color,
                                                '&:hover': {
                                                    bgcolor: option.color,
                                                    filter: 'brightness(0.9)',
                                                },
                                            }}
                                        >
                                            {option.button}
                                        </Button>
                                    </CardActions>
                                </Card>
                            );
                        })}
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );

};

export default MainApp; 